import { Loader2, Save, Send, Sparkles } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";

function EditorBanners({ req, completed, paymentAccepted }) {
  return (
    <>
      {!completed && !paymentAccepted && (
        <div className="rounded-xl bg-amber-50 border border-amber-300 p-4 mb-4 text-sm text-amber-900" data-testid="payment-gate-banner">
          Primero verifica y acepta el comprobante de pago del paciente para poder generar
          y enviar la receta.
        </div>
      )}
      {req.ai_status === "generating" && (
        <div className="rounded-xl bg-accent/50 border border-primary/20 p-4 mb-4 text-sm flex items-center gap-3" data-testid="ai-generating-banner">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          La IA está leyendo los laboratorios y la receta previa. Esto puede tardar 1-2 minutos...
        </div>
      )}
      {req.ai_status === "error" && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 mb-4 text-sm text-red-700" data-testid="ai-error-banner">
          Error de la IA: {req.ai_error}. Puedes reintentar o escribir manualmente.
        </div>
      )}
    </>
  );
}

function GenerateButton({ req, paymentAccepted, onGenerate }) {
  const hasDraft = !!req.draft;
  const btn = (
    <Button
      variant="outline"
      size="sm"
      className="rounded-full"
      disabled={req.ai_status === "generating" || !paymentAccepted}
      onClick={hasDraft ? undefined : onGenerate}
      data-testid="generate-ai-btn"
    >
      {req.ai_status === "generating" ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generando...
        </>
      ) : (
        <>
          <Sparkles className="h-4 w-4 mr-2 text-primary" strokeWidth={1.5} />
          {hasDraft ? "Regenerar con IA" : "Generar borrador con IA"}
        </>
      )}
    </Button>
  );
  // Sin draft → sin confirmación (clic directo). Con draft → confirmación porque sobrescribe.
  if (!hasDraft) return btn;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{btn}</AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">¿Regenerar el borrador?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta acción <span className="font-semibold">descarta los cambios que hayas hecho en el editor</span>{" "}
            y vuelve a pedirle a la IA un nuevo borrador (usando también las referencias adicionales que adjuntaste).
            Si tienes cambios importantes, guárdalos primero o cópialos antes de continuar.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid="regenerate-cancel-btn">Cancelar</AlertDialogCancel>
          <AlertDialogAction className="rounded-full" onClick={onGenerate} data-testid="regenerate-confirm-btn">
            Sí, regenerar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DraftFields({ draft, setDraft, completed }) {
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Diagnóstico (Dx)</Label>
        <Input
          value={draft.dx}
          onChange={(e) => setDraft({ ...draft, dx: e.target.value })}
          disabled={completed}
          placeholder="Seguimiento THAG #N + ..."
          data-testid="draft-dx-input"
        />
      </div>
      <Tabs defaultValue="receta">
        <TabsList className="rounded-full">
          <TabsTrigger value="receta" className="rounded-full" data-testid="tab-receta">
            Receta
          </TabsTrigger>
          <TabsTrigger value="nota" className="rounded-full" data-testid="tab-nota">
            Nota de Evolución
          </TabsTrigger>
        </TabsList>
        <TabsContent value="receta">
          <Textarea
            rows={16}
            value={draft.receta}
            onChange={(e) => setDraft({ ...draft, receta: e.target.value })}
            disabled={completed}
            placeholder="1. Medicamento... Aplicaciones: ..."
            className="font-mono text-[13px] leading-relaxed"
            data-testid="draft-receta-textarea"
          />
        </TabsContent>
        <TabsContent value="nota">
          <Textarea
            rows={16}
            value={draft.nota_evolucion}
            onChange={(e) => setDraft({ ...draft, nota_evolucion: e.target.value })}
            disabled={completed}
            placeholder="Presentación: ... Subjetivo: ... BH: Leu ... | Eri ..."
            className="font-mono text-[13px] leading-relaxed"
            data-testid="draft-nota-textarea"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EditorActions({ draft, paymentAccepted, saving, finalizing, onSave, onFinalize }) {
  return (
    <div className="flex flex-wrap justify-end gap-3 mt-5">
      <Button variant="outline" className="rounded-full" onClick={onSave} disabled={saving || !paymentAccepted} data-testid="save-draft-btn">
        <Save className="h-4 w-4 mr-2" strokeWidth={1.5} /> {saving ? "Guardando..." : "Guardar borrador"}
      </Button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            className="rounded-full"
            disabled={finalizing || !paymentAccepted || !draft.receta.trim() || !draft.nota_evolucion.trim()}
            data-testid="finalize-btn"
          >
            <Send className="h-4 w-4 mr-2" strokeWidth={1.5} /> Enviar al paciente
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">¿Enviar receta final?</AlertDialogTitle>
            <AlertDialogDescription>
              Se generará el PDF con la receta y nota de evolución, y el paciente será
              notificado. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="finalize-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full" onClick={onFinalize} data-testid="finalize-confirm-btn">
              Sí, enviar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function DraftEditor({ req, draft, setDraft, completed, paymentAccepted,
                              saving, finalizing, onGenerate, onSave, onFinalize }) {
  return (
    <div className="border rounded-2xl bg-white p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-heading font-semibold">Receta y Nota de Evolución</p>
        {!completed && <GenerateButton req={req} paymentAccepted={paymentAccepted} onGenerate={onGenerate} />}
      </div>

      <EditorBanners req={req} completed={completed} paymentAccepted={paymentAccepted} />
      <DraftFields draft={draft} setDraft={setDraft} completed={completed} />

      {!completed && (
        <EditorActions
          draft={draft}
          paymentAccepted={paymentAccepted}
          saving={saving}
          finalizing={finalizing}
          onSave={onSave}
          onFinalize={onFinalize}
        />
      )}

      {completed && (
        <div className="mt-5 flex justify-end">
          <a href={`${API}/requests/${req.id}/pdf`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full" data-testid="pro-download-pdf-btn">
              Ver PDF final
            </Button>
          </a>
        </div>
      )}
    </div>
  );
}
